// Shared styles for admin form components (used by organizations, properties, etc.)
import { StyleSheet } from 'react-native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

/**
 * Theme-aware shared form stylesheet hook.
 *
 * Pulls colors from `admin.formInput`, `admin.dataTable`, and the shared
 * `redesign.accent.red` tokens so light/dark toggle is automatic. Layout
 * dimensions match the static `sharedFormStyles` export below for drop-in
 * compatibility — feature migrations swap their imports as they're touched.
 */
export const useSharedFormStyles = () =>
  useThemedStyles(({ components, colors, semantic }) => {
    const admin = components.admin;
    return StyleSheet.create({
      // Container
      container: {
        flex: 1,
      },

      // Section styles
      section: {
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
      },
      sectionTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: admin.dataTable.text,
        marginBottom: 12,
      },
      sectionSubtitle: {
        fontSize: 13,
        color: colors.text.dim,
        marginBottom: 16,
      },

      // Field styles
      field: {
        marginBottom: 20,
      },
      label: {
        fontSize: 14,
        fontWeight: '500',
        color: admin.formInput.label,
        marginBottom: 8,
      },

      // Input styles
      input: {
        borderWidth: 1,
        borderColor: admin.formInput.border,
        borderRadius: 8,
        padding: 12,
        fontSize: 14,
        color: admin.formInput.text,
        backgroundColor: admin.formInput.bg,
      },
      inputFocused: {
        borderColor: admin.formInput.borderFocused,
      },
      inputError: {
        borderColor: semantic.status.error,
      },

      // Text styles
      errorText: {
        color: semantic.status.error,
        fontSize: 12,
        marginTop: 4,
      },
      helpText: {
        color: colors.text.dim,
        fontSize: 12,
        marginTop: 4,
      },

      // Button container
      actionsContainer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        padding: 24,
        gap: 12,
      },

      // Base button
      button: {
        paddingVertical: 12,
        paddingHorizontal: 20,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 44,
        minWidth: 120,
      },

      // Button variants — colors driven by admin tokens, not legacy admin color constants
      cancelButton: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: admin.dataTable.border,
      },
      cancelButtonText: {
        color: admin.dataTable.text,
        fontSize: 14,
        fontWeight: '600',
      },
      createButton: {
        backgroundColor: admin.formInput.borderFocused,
      },
      createButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '600',
      },
      saveButton: {
        backgroundColor: admin.formInput.borderFocused,
      },
      saveButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '600',
      },
      deleteButton: {
        backgroundColor: semantic.status.error,
      },
      deleteButtonText: {
        color: '#fff',
        fontSize: 14,
        fontWeight: '600',
      },
      disabledButton: {
        opacity: 0.5,
      },

      // Loading
      loadingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        gap: 12,
      },
      loadingText: {
        fontSize: 14,
        color: colors.text.dim,
      },
    });
  });
